// dashboard.regions.$regionId.tsx — T066
// Route : /dashboard/regions/:regionId — rôles ayant regionDashboard dans la matrice + accès périmètre.
// Rendu via l'Outlet du layout parent dashboard.regions.tsx (qui fournit AppLayout) —
// ne pas ré-envelopper dans <AppLayout> ici.

import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { z } from "zod";

import { useAuth } from "@/application/contexts/AuthContext";
import { usePermissions } from "@/application/hooks/usePermissions";
import { useSites } from "@/application/hooks/useSites";
import { DashboardRegionPage } from "@/presentation/pages/DashboardRegionPage";

const dashboardRegionSearchSchema = z.object({
  siteSize: z.string().min(1).optional(),
});

interface DashboardRegionSearch {
  siteSize?: string;
}

export const Route = createFileRoute("/dashboard/regions/$regionId")({
  component: function DashboardRegionComponent() {
    const { regionId } = Route.useParams();
    const search = Route.useSearch();
    const { isLoading } = useAuth();
    const permissions = usePermissions();
    const { data: sites, isLoading: sitesLoading } = useSites({ regionId });
    const navigate = useNavigate();
    const navigateWithFilter = Route.useNavigate();

    const hasAccessibleSiteInRegion = (sites ?? []).some(
      (site) => site.regionId === regionId && !site.isArchived,
    );
    const canViewRegion =
      permissions.duerp.canViewRegionDashboard(regionId) ||
      (permissions.duerp.canViewRegions() && hasAccessibleSiteInRegion);

    useEffect(() => {
      if (!isLoading && !sitesLoading && !canViewRegion) {
        void navigate({ to: "/" });
      }
    }, [canViewRegion, isLoading, navigate, sitesLoading]);

    return (
      <DashboardRegionPage
        regionId={regionId}
        siteSizeFilter={search.siteSize}
        onSiteSizeFilterChange={(siteSize) =>
          void navigateWithFilter({
            search: { siteSize: siteSize || undefined },
            replace: true,
          })
        }
      />
    );
  },
  validateSearch: (search: Record<string, unknown>): DashboardRegionSearch => {
    const parsed = dashboardRegionSearchSchema.safeParse(search);
    return parsed.success ? parsed.data : {};
  },
});
